import { useCallback, useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTenant } from "../contexts/TenantContext";
import { Property, SearchFilters } from "../types/search";
import { useSearchQuery } from "../hooks/search/useSearchQuery";
import { useDirectSearchQuery } from "../hooks/search/useDirectSearchQuery";
import { LandingSearchBox } from "../components/features/search/LandingSearchBox";
import SearchResults from "../components/features/search/SearchResults";
import MapView from "../components/features/search/MapView";
import type { ViewMode } from "../components/features/search/ViewToggle";
import ViewToggle from "../components/features/search/ViewToggle";
import { Loader } from "lucide-react";
import { useMLSLogos } from "../hooks/mls/useMLSLogos";
import { analyticsService } from "../services/analyticsService";
import { resolveSearchCity } from "../utils/searchTransitionUtils";
import {
  parseCitySlug,
  findSubscribedCity,
  isCityLocked as computeCityLocked,
  titleCaseSegment,
} from "../utils/subscribedCities";

// Feature slugs handled with explicit aiSearch filters rather than a text query.
// This is the client-side feature→filter map that used to live in the per-city
// Firestore `pages/{featureSlug}` docs; the set is bounded and curated so the
// page needs no Firestore read to know how to filter each landing route.
const DIRECT_FILTER_SLUGS = [
  "homes-for-sale",
  "town-homes-for-sale",
  "condos-for-sale",
  "homes-for-sale-with-pool",
  "3-bedroom-homes-for-sale",
  "4-bedroom-homes-for-sale",
  "5-bedroom-homes-for-sale",
  "homes-for-sale-with-waterfront",
  "condos-for-sale-with-waterfront",
];

// Tenants whose non-approved MLS means the city/feature text query must be empty
// (scores are force-shown instead). Preserved from the previous implementation.
const EXCLUDE_FROM_QUERY_TENANTS = [
  "T3LfnzHu7k7qUNmeJlza", // Willow Manor
  "q7sS2M35TF4VnGtHEPoz", // Daron Campbell
  "E1CiupOMkVSsWa6hbRtr", // Key Home Sale
  "xUJfOVZZyt3ppvTZiHEA", // CCL Real Estate
  "NTn2XhrM8VUm9OA6r0zB", // The Stern Team
  "jU2KBf0bfA7jHfeu1w6H", // Josh Canova
  "6MISBDqukKX2HQBx6A7N", // KW San Diego Home Team
  "wcMON957ixcicqDfI3pU", // Beverly Shanahan
  "J7eupMgWuLLkLlD8xJYP", // Holden Parker
  "OxG7YEYsotxtdPj7ixNm", // Curtis
  "LCXemW5c5itmWG0uAt9X", // The Duley Group
];

// Map a curated feature slug to its aiSearch filter set. Mirrors the filters the
// Firestore landing-page generator baked into each `pages/{featureSlug}` doc.
const buildFeatureFilters = (featureSlug: string): Record<string, any> => {
  const searchFilters: Record<string, any> = { standard_status: "Active" };

  if (!DIRECT_FILTER_SLUGS.includes(featureSlug)) {
    const isCondoSearch = featureSlug
      .replace(/-/g, " ")
      .toLowerCase()
      .includes("condo");
    searchFilters.property_sub_type = isCondoSearch
      ? "Condominium"
      : "Single Family Residence";
    return searchFilters;
  }

  switch (featureSlug) {
    case "homes-for-sale":
      searchFilters.property_sub_type = "Single Family Residence";
      break;
    case "town-homes-for-sale":
      searchFilters.property_sub_type = "Townhouse";
      break;
    case "condos-for-sale":
      searchFilters.property_sub_type = "Condominium";
      break;
    case "3-bedroom-homes-for-sale":
      searchFilters.bedrooms = 3;
      searchFilters.property_sub_type = "Single Family Residence";
      break;
    case "4-bedroom-homes-for-sale":
      searchFilters.bedrooms = 4;
      searchFilters.property_sub_type = "Single Family Residence";
      break;
    case "5-bedroom-homes-for-sale":
      searchFilters.bedrooms = 5;
      searchFilters.property_sub_type = "Single Family Residence";
      break;
    case "homes-for-sale-with-pool":
      searchFilters.pool = true;
      searchFilters.property_sub_type = "Single Family Residence";
      break;
    case "homes-for-sale-with-waterfront":
      searchFilters.waterfront = true;
      searchFilters.property_sub_type = "Single Family Residence";
      break;
    case "condos-for-sale-with-waterfront":
      searchFilters.waterfront = true;
      searchFilters.property_sub_type = "Condominium";
      break;
  }

  return searchFilters;
};

export const DynamicLandingPage = () => {
  const { citySlug, featureSlug: rawFeatureSlug } = useParams<{
    citySlug: string;
    featureSlug: string;
  }>();

  const featureSlug = rawFeatureSlug || "";

  const { config, isLoading: isTenantLoading } = useTenant();
  const navigate = useNavigate();

  // City/state parsed from the slug, e.g. "colorado-springs-co" → "Colorado Springs","CO".
  const { cityName: slugCityName, stateCode: slugStateCode } = useMemo(
    () => parseCitySlug(citySlug),
    [citySlug]
  );

  // Match the slug against the tenant's subscribed cities (public tenant-config,
  // Spanner-backed). IDX landing pages resolve only for subscribed cities.
  const subscribedCity = useMemo(
    () =>
      findSubscribedCity(config?.subscribedCities, slugCityName, slugStateCode),
    [config?.subscribedCities, slugCityName, slugStateCode]
  );

  // Lock when the tenant has subscriptions and this city isn't one of them; an
  // empty list stays lenient (see isCityLocked in utils/subscribedCities).
  const isCityLocked = useMemo(
    () =>
      computeCityLocked(config?.subscribedCities, slugCityName, slugStateCode),
    [config?.subscribedCities, slugCityName, slugStateCode]
  );

  // Prefer the canonical city name from Spanner (via tenant-config) when the
  // city is subscribed; fall back to the slug-derived name otherwise.
  const effectiveCityName = subscribedCity?.name || slugCityName;

  const [seo, setSeo] = useState<{
    title: string;
    description: string;
    schema: object;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("combined");
  const [geocodedResults, setGeocodedResults] = useState<Property[]>([]);
  const [displayQuery, setDisplayQuery] = useState("");
  //>>>> TEMP
  const [forceShowScores, setForceShowScores] = useState(false);

  // Track current search query to determine which hook to use
  const [currentQuery, setCurrentQuery] = useState("");

  // Initialize both search hooks
  const regularSearch = useSearchQuery();
  const directSearch = useDirectSearchQuery();

  // Determine if we should use direct search (no query) or regular search
  const isDirectSearch = !currentQuery;

  // Use the appropriate search hook based on whether we have a query or not
  const {
    results,
    loading: searchLoading,
    error: searchError,
    hasMore,
    search,
    loadMore,
    filters,
    totalAvailable = 0,
    retryFailedRequest,
  } = isDirectSearch ? directSearch : regularSearch;

  //>>>> TEMP ORIG
  // Filter out properties that don't have "Active" status
  const filteredResults = useMemo(() => {
    return results.filter((property) => {
      // Check if the property has an "Active" listing status
      return property.listingStatus === "Active";
    });
  }, [results]);

  // Fetch MLS logos for all properties
  const { mlsLogosMap, mlsComplianceQueries } = useMLSLogos(filteredResults);

  // Track page view
  useEffect(() => {
    if (!config || !citySlug || !featureSlug) {
      return;
    }

    const orgId = config?.id || "default";
    const tenantName = config.name || "Unknown";

    analyticsService.trackEvent({
      tenantId: config.id,
      eventType: "pageview",
      category: "Landing Page",
      action: "View",
      userType: "visitor",
      data: {
        path: window.location.pathname,
        citySlug,
        featureSlug,
        title: document.title,
        referrer: document.referrer,
        tenantName,
        orgId,
        isAuthenticated: false,
      },
    });
  }, [config, citySlug, featureSlug]);

  // Build SEO copy + kick off the aiSearch listing query. City/state are derived
  // from the URL slug and the feature→filter mapping is resolved client-side, so
  // this page needs no Firestore/tenant-content read — aiSearch listings are the
  // only server data. (The Spanner city() GraphQL is auth-gated and these pages
  // are public/crawler-facing, so the slug is the source of truth here.)
  useEffect(() => {
    if (isTenantLoading || !config?.id || !citySlug || !featureSlug) {
      return;
    }

    // City isn't one of the tenant's subscribed cities — skip the listing fetch;
    // the page renders a "not available" state instead (see render below).
    if (isCityLocked) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // City + state resolved at the top of the component: the canonical Spanner
      // city name when subscribed, else the slug-derived name; state from the slug.
      const stateCode = slugStateCode;
      const properCityName = effectiveCityName;

      // Feature name from the slug, e.g. "4-bedroom-homes-for-sale" →
      // "4 Bedroom Homes For Sale".
      const featureName = featureSlug
        .split("-")
        .map(titleCaseSegment)
        .join(" ");

      // SEO copy from built-in templates. Tenant-specific landing templates were
      // never migrated off Firestore; this generic copy is their documented
      // fallback and is sufficient for crawler indexing.
      const seoTitle = `${featureName} in ${properCityName}, ${stateCode.toUpperCase()} | Property Search`;
      const seoDescription = `Search ${featureName} in ${properCityName}, ${stateCode.toUpperCase()}. Browse current listings and market data.`;
      const seoSchema = {
        "@context": "https://schema.org",
        "@type": "RealEstateAgent",
        name: config.name || "Property Search",
        description: `Browse ${featureName} in ${properCityName}, ${stateCode.toUpperCase()}`,
      };
      setSeo({
        title: seoTitle,
        description: seoDescription,
        schema: seoSchema,
      });

      // Reflect the SEO copy into the document head for crawlers.
      document.title = seoTitle;
      const metaDescription = document.querySelector(
        'meta[name="description"]',
      );
      if (metaDescription) {
        metaDescription.setAttribute("content", seoDescription);
      }
      const existingSchema = document.querySelector("#schema-markup");
      if (existingSchema) existingSchema.remove();
      const schemaScript = document.createElement("script");
      schemaScript.id = "schema-markup";
      schemaScript.type = "application/ld+json";
      schemaScript.text = JSON.stringify(seoSchema);
      document.head.appendChild(schemaScript);

      // Excluded tenants (non-approved MLS) search with an empty query + shown scores.
      const excludeFromQuery = EXCLUDE_FROM_QUERY_TENANTS.includes(config.id);
      const useDirectFilters = DIRECT_FILTER_SLUGS.includes(featureSlug);
      setForceShowScores(excludeFromQuery);

      const initialQuery = excludeFromQuery
        ? ""
        : `${properCityName} ${stateCode.toUpperCase()} ${featureSlug.replace(
            /-/g,
            " ",
          )}`.trim();
      setDisplayQuery(initialQuery);

      search({
        ...filters,
        query:
          excludeFromQuery || useDirectFilters
            ? ""
            : featureSlug.replace(/-/g, " ").trim(),
        tenant_id: config.id,
        location: {
          city: properCityName,
          state: stateCode.toUpperCase(),
        },
        filters: buildFeatureFilters(featureSlug),
      });

      analyticsService.trackEvent({
        tenantId: config.id,
        eventType: "pageview",
        category: "Landing Page",
        action: "View",
        userType: "visitor",
        data: {
          path: window.location.pathname,
          cityId: citySlug.toLowerCase(),
          featureSlug,
          hasCustomization: false,
          title: seoTitle,
          referrer: document.referrer,
          tenantName: config.name,
          orgId: config?.id || "default",
          isAuthenticated: false,
        },
      });
    } catch (err) {
      const loadError =
        err instanceof Error ? err : new Error("Failed to load page");
      console.error("Page load failed:", loadError);
      setError(loadError);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config?.id,
    citySlug,
    featureSlug,
    isTenantLoading,
    isCityLocked,
    effectiveCityName,
    slugStateCode,
  ]);

  // Map coordinates effect
  useEffect(() => {
    const propertiesWithCoordinates = filteredResults.map((property) => ({
      ...property,
      address: {
        ...property.address,
        coordinates:
          property.property?.latitude && property.property?.longitude
            ? {
                lat: parseFloat(property.property.latitude),
                lng: parseFloat(property.property.longitude),
              }
            : undefined,
      },
    }));
    setGeocodedResults(propertiesWithCoordinates);
  }, [filteredResults]);

  // Parse location from slug (e.g., "san-francisco-ca")
  const slugParts = citySlug?.split("-") || [];
  const stateCode = slugParts[slugParts.length - 1];
  const cityName = slugParts.slice(0, -1).join(" ");
  const properCityName = cityName
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const locationContext =
    cityName && stateCode
      ? `in ${properCityName} ${stateCode.toUpperCase()}`
      : "";

  const handleSearch = useCallback(
    (newFilters: SearchFilters) => {
      if (!config || !locationContext) return;

      const orgId = config?.id || "default";
      const tenantName = config.name || "Unknown";

      const searchQuery = newFilters.query || "";

      // Update current query for determining which search hook to use
      setCurrentQuery(searchQuery);

      // Determine if this is a direct search (no query) or regular search
      const isNewDirectSearch = !searchQuery;

      // Track search event
      analyticsService.trackEvent({
        tenantId: config.id,
        eventType: "search",
        category: "Property Search",
        action: "Execute Search",
        userType: "visitor",
        data: {
          searchQuery,
          searchFilters: newFilters,
          path: window.location.pathname,
          citySlug,
          featureSlug,
          tenantName,
          orgId,
          isAuthenticated: false,
        },
      });

      // Use the appropriate search function based on whether a query is provided
      const searchFn = isNewDirectSearch
        ? directSearch.search
        : regularSearch.search;

      // Create new filters object with search query and required parameters
      searchFn({
        ...newFilters,
        query: searchQuery,
        tenant_id: config.id,
        location: {
          city: resolveSearchCity(newFilters, properCityName),
          state: stateCode.toUpperCase(),
        },
      });
    },
    [
      regularSearch.search,
      directSearch.search,
      config,
      citySlug,
      featureSlug,
      locationContext,
    ],
  );

  const handlePropertyClick = useCallback(
    (property: Property) => {
      if (!config) return;

      const orgId = config?.id || "default";
      const tenantName = config.name || "Unknown";

      // Track property click event
      analyticsService.trackEvent({
        tenantId: config.id,
        eventType: "interaction",
        category: "Property Interaction",
        action: "View Property",
        userType: "visitor",
        data: {
          propertyId: property.id,
          path: window.location.pathname,
          searchQuery: filters.query,
          searchFilters: filters,
          citySlug,
          featureSlug,
          tenantName,
          orgId,
          isAuthenticated: false,
        },
      });

      // Open property in new tab with additional query parameters
      const tenantId = config.id || "";
      const mlsOsn = property.mlsBoardCode || "";
      window.open(
        `/property/${property.id}?tenant_id=${tenantId}&mls_osn=${mlsOsn}`,
        "_blank",
      );
    },
    [navigate, config, filters, citySlug, featureSlug],
  );

  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      if (!config) return;

      const orgId = config?.id || "default";
      const tenantName = config.name || "Unknown";

      // Track view mode change
      analyticsService.trackEvent({
        tenantId: config.id,
        eventType: "interaction",
        category: "View Mode",
        action: "Change View",
        userType: "visitor",
        data: {
          previousMode: viewMode,
          newMode: mode,
          path: window.location.pathname,
          citySlug,
          featureSlug,
          tenantName,
          orgId,
          isAuthenticated: false,
        },
      });

      setViewMode(mode);
    },
    [config, viewMode, citySlug, featureSlug],
  );

  if (loading || isTenantLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    console.error("Page error:", error.message);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {error.message}
          </h1>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-opacity-90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Not one of this tenant's subscribed cities — no landing page for it.
  if (isCityLocked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-lg px-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {slugCityName}
            {slugStateCode ? `, ${slugStateCode.toUpperCase()}` : ""} isn’t
            available
          </h1>
          <p className="text-gray-600 mb-6">
            This area isn’t part of {config?.name || "this site"}’s coverage.
            Browse available listings on the buy page.
          </p>
          <button
            onClick={() => navigate("/search")}
            className="bg-primary text-white font-medium rounded-md hover:bg-opacity-90 transition-all inline-flex items-center justify-center gap-2"
          >
            Browse listings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 min-h-screen bg-gray-50">
      <div className="pb-8">
        <div className="flex flex-col  max-w-[1600px] 3xl:max-w-[1800px] mx-auto">
          <div className="order-0 pt-8 pb-10">
            <h1 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-2 xl:mb-3">
              {seo?.title}
            </h1>
            <p className="text-base lg:text-xl text-gray-700">
              {seo?.description}
            </p>
          </div>

          <div className="order-1">
            <div
              className="py-3 sticky top-20 z-40 bg-gray-50 shadow-sm border-b"
              style={{
                borderColor: config?.branding?.colors?.text
                  ? `${config.branding.colors.text}11`
                  : "#00000011",
              }}
            >
              <div>
                <div className="flex flex-col lg:flex-row items-end lg:items-center lg:justify-between py-2 gap-2">
                  <div className="w-full">
                    <LandingSearchBox
                      onSearch={handleSearch}
                      initialFilters={{ ...filters, query: displayQuery }}
                      isLoading={searchLoading}
                      onQueryChange={setDisplayQuery}
                      showFilters={true}
                      filterBorderColor={config?.branding?.colors?.primary}
                      filterTextColor={config?.branding?.colors?.primary}
                    />
                  </div>

                  <div className="flex justify-end lg:justify-start">
                    <ViewToggle
                      currentView={viewMode}
                      onViewChange={handleViewModeChange}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1 relative overflow-hidden mt-5 lg:mt-12">
              {viewMode === "properties" && (
                <div className="w-full">
                  <SearchResults
                    results={filteredResults}
                    loading={searchLoading}
                    error={searchError}
                    hasMore={hasMore}
                    onLoadMore={loadMore}
                    onPropertyClick={handlePropertyClick}
                    totalAvailable={totalAvailable}
                    onRetry={retryFailedRequest}
                    filters={filters?.filters}
                    searchQuery={filters?.query}
                    forceShowScores={forceShowScores}
                    pageTitle={`Found ${totalAvailable.toLocaleString()} ${
                      seo?.title
                    }`}
                    hideGoogleSignIn={true}
                    mlsLogosMap={mlsLogosMap}
                    mlsComplianceQueries={mlsComplianceQueries}
                  />
                </div>
              )}

              {viewMode === "map" && (
                <div className="w-full h-[600px]">
                  <MapView
                    properties={geocodedResults}
                    onPropertySelect={handlePropertyClick}
                    mlsLogosMap={mlsLogosMap}
                  />
                </div>
              )}

              {viewMode === "combined" && (
                <div className="flex flex-col md:flex-row gap-8 relative">
                  <div
                    className="w-full md:w-1/2 md:overflow-y-auto md:max-h-[calc(100vh-200px)] md:pr-4 md:scrollbar-thin md:scrollbar-thumb-gray-300 md:scrollbar-track-gray-100 md:hover:scrollbar-thumb-gray-400"
                    style={{
                      borderColor: config?.branding?.colors?.text
                        ? `${config.branding.colors.text}11`
                        : "#00000011",
                    }}
                  >
                    <SearchResults
                      results={filteredResults}
                      loading={searchLoading}
                      error={searchError}
                      hasMore={hasMore}
                      onLoadMore={loadMore}
                      onPropertyClick={handlePropertyClick}
                      totalAvailable={totalAvailable}
                      onRetry={retryFailedRequest}
                      gridColumns={2}
                      filters={filters?.filters}
                      searchQuery={filters?.query}
                      forceShowScores={forceShowScores}
                      pageTitle={`Found ${totalAvailable.toLocaleString()} ${
                        seo?.title
                      }`}
                      hideGoogleSignIn={true}
                      mlsLogosMap={mlsLogosMap}
                      mlsComplianceQueries={mlsComplianceQueries}
                    />
                  </div>
                  <div className="w-full md:w-1/2 h-[calc(100vh-200px)] md:sticky md:top-[200px] bg-white rounded-lg shadow-sm">
                    <MapView
                      properties={geocodedResults}
                      onPropertySelect={handlePropertyClick}
                      mlsLogosMap={mlsLogosMap}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicLandingPage;
